import { useLocalization } from "@tm/localization"
import { Article, AvailabilityComponentProps, channel, DefaultArticleItemState, ErpInformation, SupplierArticleDto } from "@tm/models"
import { copyToClipboard } from "@tm/utils"
import { isEqual } from "lodash"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDmsArticleInfo } from "@bundles/dms"
import { AvailabilityWrapper as AvailabilityComponent } from "@bundles/erp"
import { useArticleListConfiguration } from "../../ArticleListConfiguration"
import {
    useActiveVehicleDataProvidersContext,
    useBasketQuantitiesContext,
    useErpInfosContext,
    useListOptionsContext,
    useListParamsContext,
    usePreviouslyOrderedArticlesContext,
    useProductGroupRepairTimesDataContext,
    useSupplierLogosContext,
} from "../../ContextProvider"
import { useArticleListActionsContext } from "../../ContextProvider/ArticleListActions"
import { useFiltersContext } from "../../ContextProvider/Filters"
import { useVehicleRecordsContext } from "../../VehicleParts/ContextProvider"
import { determineTmaOriginByCrossSellingModal, isAddButtonDisabled } from "../../helpers"
import { useArticleActions } from "./useArticleActions"
import { useArticleImages } from "../useArticleImages"
import { useArticleSelection } from "../useArticleSelection"
import { useLoadErpInfos } from "../useErpInfos"
import { useGetRepairTimesUrl } from "../useGetRepairTimesUrl"
import { useHandleClickAttribute } from "../useHandleClickAttribute"
import { useHandleClickReference } from "../useHandleClickReference"
import { useVehicle } from "../useVehicle"
import { useArticleExtendedDeliveryInfo } from "./useArticleExtendedDeliveryInfo"
import { useArticleFoundBy } from "./useArticleFoundBy"
import { useArticleNotes } from "./useArticleNotes"
import { useArticleNotifications } from "./useArticleNotifications"
import { useArticleOptions } from "./useArticleOptions"
import { useArticleTradeReferences } from "./useArticleTradeReferences"
import { useArticleErpInfos } from "./useArticlesErpInfos"
import { useDisplayArticle } from "./useDisplayArticle"
import { useHandleAddToBasket } from "./useHandleAddToBasket"
import { useHandleAddToCostEstimation } from "./useHandleAddToCostEstimation"
import { useHandleClickAvailability } from "./useHandleClickAvailability"
import { useHandleClickDetails } from "./useHandleClickDetails"
import { useHandleClickImage } from "./useHandleClickImage"
import { useHandleRemoveFromBasket } from "./useHandleRemoveFromBasket"
import { useTraderErpInfos } from "./useTraderErpInfos"
import { useFavoriteArticleList } from "./useFavoriteArticleList"
import { useArticleListFeaturesDisable } from "../../ArticleListConfiguration/ArticleListFeaturesDisable"
import { CombinedParticularState, useParticularContext } from "../../../PureList/ContextProviders/Particular/ParticularContext"
import { usePartsAlternativesModuleState } from "../../PartsAlternatives/PartsAlternativesModuleState"
import { useCrossSellingActions } from "../../components/CrossSelling"

export function useArticleItem(article: Article, articleOrder?: number): DefaultArticleItemState {
    const { toggleInlineFilter, updateBasketQuantities, toggleSupplier, showArticleDetails } = useArticleListActionsContext()
    const {
        listType,
        attributes,
        startParams: { replaceButtonBundle, crossSellingModalType },
    } = useListParamsContext()
    const previouslyOrderedArticles = usePreviouslyOrderedArticlesContext()

    const { translateText } = useLocalization()

    const options = useArticleOptions(article)

    const [expanded, setExpanded] = useState(!options.compactView)
    const [traderSelection, setTraderSelection] = useState(false)

    const toggleExpand = useCallback(
        (expand?: boolean) => {
            if (options.tileView) {
                options.toggleExpandTile?.(article.id)
            } else {
                setExpanded((prev) => (expand !== undefined ? expand : !prev))
            }
        },
        [article.id, options]
    )

    const toggleTraderSelection = useCallback(() => {
        setTraderSelection((prev) => !prev)
    }, [])

    const disables = useArticleListFeaturesDisable()
    const isVehicleDependent = listType === "vehicle"
    const erpInfosData = useErpInfosContext()
    const { basketQuantities } = useBasketQuantitiesContext()
    const vehicleRecords = useVehicleRecordsContext(false)
    const vehicle = useVehicle()
    const [articleQuantity, setArticleQuantity] = useState(article.quantity || 1)
    const [originalQuantity, setOriginalQuantity] = useState(article.quantity || 1)
    const articleErpInfos = useArticleErpInfos(article, articleQuantity, erpInfosData)
    const defaultArticleErpInfoResponse = articleErpInfos.default?.state === "success" ? articleErpInfos.default.response : undefined
    const handleClickDetails = useHandleClickDetails(article, articleQuantity)
    const handleClickAvailability = useHandleClickAvailability(article, articleQuantity, erpInfosData)
    const { handleClickImage, handleCloseImage, showImageViewer } = useHandleClickImage(article)
    const handleClickAttribute = useHandleClickAttribute(article, handleClickDetails, toggleInlineFilter)
    const handleClickReference = useHandleClickReference(article, articleQuantity, showArticleDetails)
    const addToBasket = useHandleAddToBasket(article)
    const removeFromBasket = useHandleRemoveFromBasket()
    const articleImages = useArticleImages(article)
    const tradeReferences = useArticleTradeReferences(article)
    const loadErpInfos = useLoadErpInfos(erpInfosData, tradeReferences)
    const traderErpInfos = useTraderErpInfos(article, erpInfosData, tradeReferences)
    const notifications = useArticleNotifications(articleErpInfos.default)
    const favoriteArticleList = useFavoriteArticleList(article)
    const foundBy = useArticleFoundBy(article)
    const selection = useArticleSelection()
    const isAlternativeSearch = usePartsAlternativesModuleState((x) => !!x)
    const { hideExtendedDeliveryComponent, feedbackConfiguration, enableArticleNotes, positionChangeEArtNrHArtNr, imsIcon, memoTooltipIcon } =
        useArticleListConfiguration()
    const extendedDeliveryInfo = useArticleExtendedDeliveryInfo(!hideExtendedDeliveryComponent, handleClickDetails, articleErpInfos.default)

    useEffect(() => {
        setExpanded(!options.compactView)
    }, [options.compactView])

    const basketQuantity = useMemo(
        () =>
            basketQuantities.find((q) =>
                isEqual(q.article, {
                    dataSupplierArticleNumber: article.supplierArticleNo,
                    dataSupplierId: article.supplier.id,
                    productGroupId: article.productGroup.id,
                } as SupplierArticleDto)
            ),
        [basketQuantities, article]
    )

    // Notes --------------------------------
    const notes = useArticleNotes(article)
    // --------------------------------------

    const alreadyOrdered = useMemo(() => {
        return (
            previouslyOrderedArticles?.some((x) => x.supplierArticleNumber === article.supplierArticleNo && x.supplierId === article.supplier.id) ??
            false
        )
    }, [previouslyOrderedArticles, article.supplierArticleNo, article.supplier.id])

    // CostEstimation -----------------------
    const { activeProviders } = useActiveVehicleDataProvidersContext()
    const { externalCatalogUrl } = useListOptionsContext()
    const productGroupRepairTimes = useProductGroupRepairTimesDataContext()
    const repairTimeProviders = productGroupRepairTimes.productGroupRepairTimes?.productGroups[article.productGroup.id]

    const addButtonsDisabled = useMemo(() => isAddButtonDisabled(articleErpInfos, article.requestErpInfo), [articleErpInfos, article.requestErpInfo])

    const addToKva = useHandleAddToCostEstimation(
        article,
        addToBasket,
        activeProviders,
        externalCatalogUrl,
        repairTimeProviders,
        basketQuantity?.isInOrder
    )
    // --------------------------------------

    const getRepairTimesUrl = useGetRepairTimesUrl()
    const repairTimesUrl = useMemo(() => {
        if (!!repairTimeProviders?.length && repairTimeProviders) {
            return getRepairTimesUrl(article, repairTimeProviders)
        }
    }, [repairTimeProviders, getRepairTimesUrl, article])

    const { showDocumentsInline } = useListOptionsContext()

    const dmsArticleInfo = useDmsArticleInfo(article)

    const articleActions = useArticleActions({
        article,
        articleErpInfo: articleErpInfos.default,
        feedbackConfiguration,
        isNotesEnabled: enableArticleNotes,
        listType,
        notes: notes.notes,
        repairTimesUrl,
        showDocumentsInline,
        onClickDetails: handleClickDetails,
        toggleExpand,
        toggleNotes: notes.toggle,
        hideAlternatives: disables.disableAlternatives,
        disableDeleteButton: disables.disableDeleteButton,
    })

    // Supplier data ----------------------
    const { dataSupplierFilters } = useFiltersContext()
    const isSupplierSelected = useMemo(
        () => dataSupplierFilters.find((x) => x[0].id === article.supplier.id)?.[1] ?? false,
        [article.supplier.id, dataSupplierFilters]
    )
    const { supplierLogos } = useSupplierLogosContext()
    const supplierLogo = useMemo(() => supplierLogos?.[article.supplier.id], [supplierLogos, article.supplier.id])
    // ------------------------------------

    const displayArticle = useDisplayArticle(article, attributes, supplierLogo, isSupplierSelected)
    const particularContext = useParticularContext<CombinedParticularState>()

    // CrossSelling ----------------------
    const crossSellingActions = useCrossSellingActions(listType, isAlternativeSearch, crossSellingModalType)
    // ------------------------------------

    const postAddToBasket = useCallback(async () => {
        updateBasketQuantities?.([article])
        await vehicleRecords?.handleArticleAddedToBasket(article)
        vehicleRecords?.refresh()
    }, [article, vehicleRecords, updateBasketQuantities])

    const defaultArticleErpInfo = articleErpInfos.default
    const alternativArticleErpInfo = articleErpInfos.alternative
    const particularOnChangeQuantity = particularContext?.onChangeQuantity

    const handleChangeQuantity = useCallback(
        (value: number, loadErpInfo = true) => {
            particularOnChangeQuantity?.(article, value)

            setArticleQuantity(value)
            setOriginalQuantity(value) // the only case where loadErp is false, is when division has changed the quantity and the catalog doesnt want another erprequest

            if (loadErpInfo) {
                if (defaultArticleErpInfo) {
                    loadErpInfos({ article, quantity: value, erpSystem: defaultArticleErpInfo.erpSystem })
                }
                if (alternativArticleErpInfo) {
                    loadErpInfos({ article, quantity: value, erpSystem: alternativArticleErpInfo.erpSystem })
                }
            }
        },
        [article, loadErpInfos, defaultArticleErpInfo, alternativArticleErpInfo, particularOnChangeQuantity]
    )

    const handleAddToBasket = useCallback(
        async (quantity?: number, erpInformation?: ErpInformation, erpSystemId?: number) => {
            if (!erpSystemId && tradeReferences.length > 1) {
                toggleTraderSelection()
                return
            }

            crossSellingActions.loadRecommendation([article], articleOrder)

            await addToBasket(
                quantity ?? articleQuantity,
                erpInformation ?? defaultArticleErpInfoResponse,
                erpSystemId ?? tradeReferences[0]?.erpSystemId,
                undefined,
                determineTmaOriginByCrossSellingModal(crossSellingModalType)
            )
            postAddToBasket()
            handleChangeQuantity(article.quantity)
        },
        [
            tradeReferences,
            crossSellingActions,
            article,
            articleOrder,
            addToBasket,
            articleQuantity,
            defaultArticleErpInfoResponse,
            crossSellingModalType,
            postAddToBasket,
            handleChangeQuantity,
            toggleTraderSelection,
        ]
    )

    const handleReplaceArticle = useCallback(() => {
        if (disables.disableReplaceButton) {
            return
        }
        if (particularContext?.handleReplaceButton) {
            return particularContext?.handleReplaceButton(article)
        }

        replaceButtonBundle &&
            channel("GLOBAL").publish("PARTS/REPLACE_PART", {
                part: article,
                isNewList: true,
                bundle: replaceButtonBundle,
                quantity: articleQuantity,
            })
    }, [disables.disableReplaceButton, particularContext, replaceButtonBundle, article, articleQuantity])

    const handleAddToCostEstimation = useCallback(
        async (quantity?: number, erpInformation?: ErpInformation, erpSystemId?: number) => {
            if (!erpSystemId && (tradeReferences.length ?? 0) > 1) {
                toggleTraderSelection()
                return
            }

            crossSellingActions.loadRecommendation([article], articleOrder)

            await addToKva(quantity ?? articleQuantity, erpInformation ?? defaultArticleErpInfoResponse, erpSystemId)
            postAddToBasket()
            handleChangeQuantity(article.quantity)
        },
        [
            addToKva,
            article,
            articleOrder,
            articleQuantity,
            crossSellingActions,
            defaultArticleErpInfoResponse,
            handleChangeQuantity,
            postAddToBasket,
            toggleTraderSelection,
            tradeReferences.length,
        ]
    )

    const handleRemoveFromBasket = useCallback(
        async (itemIds?: string[]) => {
            const ids = itemIds ?? basketQuantity?.articleQuantities?.allPartItemIds
            if (ids?.length) {
                await removeFromBasket(ids)
                updateBasketQuantities?.([article])

                crossSellingActions.reset()
            }
        },
        [basketQuantity?.articleQuantities?.allPartItemIds, removeFromBasket, updateBasketQuantities, article, crossSellingActions]
    )

    const handleCopyArticleNumber = useCallback(
        (number: string) => {
            copyToClipboard(number)
            channel("APP").publish("TOAST_MESSAGE/SHOW", { message: translateText(1920) })
        },
        [translateText]
    )

    const toggleSupplierFilter = useCallback(
        (exclusive?: boolean) => {
            toggleSupplier?.(article.supplier.id, exclusive)
        },
        [toggleSupplier, article.supplier.id]
    )

    const AvailabilityComponentIntern = useCallback(
        (props: AvailabilityComponentProps) => (
            <AvailabilityComponent {...props} erpSystem={erpInfosData.erpConfig?.useOrderByDistributor ? props.erpSystem : undefined} />
        ),
        [erpInfosData.erpConfig?.useOrderByDistributor]
    )

    const showExtendedAvailabilityContainer = useMemo(
        () =>
            !!erpInfosData.erpConfig?.erpSystemConfigs &&
            erpInfosData.erpConfig?.erpSystemConfigs.filter((x) => x.useForGetErpInformation).length > 1,
        [erpInfosData.erpConfig?.erpSystemConfigs]
    )

    return useMemo(() => {
        return {
            type: "default",
            isVehicleDependent,
            articleOrder,
            options,
            article,
            displayArticle,
            quantity: articleQuantity,
            originalQuantity,
            articleActions,
            articleErpInfos,
            basketQuantity,
            notes,
            tradeReferences,
            traderErpInfos,
            notifications,
            expanded,
            traderSelection,
            favoriteArticleList,
            showReplaceButton: !!replaceButtonBundle && !disables.disableReplaceButton,
            positionChangeEArtNrHArtNr,
            foundBy,
            selection: disables.disableSelection || !!replaceButtonBundle ? undefined : selection,
            extendedDeliveryInfo,
            vehicle,
            showImageViewer,
            articleImages,
            imsIcon,
            memoTooltipIcon,
            dmsArticleInfo,
            alreadyOrdered,
            showExtendedAvailabilityContainer,
            addToCostEstimationButtonDisabled: addButtonsDisabled,
            addToBasketButtonDisabled: addButtonsDisabled,
            showDescriptionCompactView: !disables.disableDescriptionCompactView,
            handleClickImage,
            handleCloseImage,
            handleClickDetails,
            handleChangeQuantity,
            handleClickReference,
            handleClickAttribute,
            handleAddToBasket,
            handleAddToCostEstimation,
            handleRemoveFromBasket,
            handleReplaceArticle,
            handleCopyArticleNumber,
            handleClickAvailability,
            toggleExpand,
            toggleTraderSelection,
            toggleSupplierFilter,
            AvailabilityComponent: AvailabilityComponentIntern,
        }
    }, [
        isVehicleDependent,
        articleOrder,
        options,
        article,
        displayArticle,
        articleQuantity,
        originalQuantity,
        articleActions,
        articleErpInfos,
        basketQuantity,
        notes,
        tradeReferences,
        traderErpInfos,
        notifications,
        expanded,
        traderSelection,
        favoriteArticleList,
        replaceButtonBundle,
        disables.disableReplaceButton,
        disables.disableSelection,
        disables.disableDescriptionCompactView,
        positionChangeEArtNrHArtNr,
        foundBy,
        selection,
        extendedDeliveryInfo,
        vehicle,
        showImageViewer,
        articleImages,
        imsIcon,
        memoTooltipIcon,
        dmsArticleInfo,
        alreadyOrdered,
        showExtendedAvailabilityContainer,
        addButtonsDisabled,
        handleClickImage,
        handleCloseImage,
        handleClickDetails,
        handleChangeQuantity,
        handleClickReference,
        handleClickAttribute,
        handleAddToBasket,
        handleAddToCostEstimation,
        handleRemoveFromBasket,
        handleReplaceArticle,
        handleCopyArticleNumber,
        handleClickAvailability,
        toggleExpand,
        toggleTraderSelection,
        toggleSupplierFilter,
        AvailabilityComponentIntern,
    ])
}
